import React, { useState, useEffect, useMemo } from 'react';
import { Search, RotateCcw } from 'lucide-react';
import { usePreferencesStore } from '../../store/preferencesStore';
import {
  SHORTCUT_COMMANDS,
  getShortcutCommand,
  resolveShortcutKeys,
  shortcutTokens,
  SHORTCUT_GROUPS,
  keysFromEvent,
  setCapturingShortcut,
} from '../../config/shortcuts';

export const KeyboardTab = ({ t }) => {
  const [query, setQuery] = useState('');
  const [capturingId, setCapturingId] = useState(null);
  const [notice, setNotice] = useState(null); // { id, displaced: string[] }
  const keyboardShortcuts = usePreferencesStore((s) => s.keyboardShortcuts);
  const setKeyboardShortcut = usePreferencesStore((s) => s.setKeyboardShortcut);
  const resetKeyboardShortcut = usePreferencesStore((s) => s.resetKeyboardShortcut);
  const resetAllKeyboardShortcuts = usePreferencesStore((s) => s.resetAllKeyboardShortcuts);

  // Grouped, so extension shortcuts sit under the extension they belong to
  // rather than in one undifferentiated list. Empty groups are dropped, which
  // is what makes the search box still read as a flat filter.
  const groups = useMemo(() => {
    const q = query.toLowerCase().trim();
    const matches = q
      ? SHORTCUT_COMMANDS.filter((c) => t(c.labelKey).toLowerCase().includes(q))
      : SHORTCUT_COMMANDS;
    return SHORTCUT_GROUPS.map((g) => ({
      ...g,
      commands: matches.filter((c) => c.group === g.id),
    })).filter((g) => g.commands.length > 0);
  }, [query, t]);

  // While recording, capture the next valid combo. Suppress global handlers so the
  // pressed keys don't also fire actions (capture-phase + stopPropagation + module flag).
  useEffect(() => {
    if (!capturingId) return;
    setCapturingShortcut(true);
    const onKeyDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Escape') {
        setCapturingId(null);
        return;
      }
      const keys = keysFromEvent(e);
      if (!keys) return; // wait for a valid combo (modifier + key)
      const displaced = setKeyboardShortcut(capturingId, keys) || [];
      setNotice(displaced.length ? { id: capturingId, displaced } : null);
      setCapturingId(null);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      setCapturingShortcut(false);
    };
  }, [capturingId, setKeyboardShortcut]);

  return (
    <>
      <div className="settings-shortcut-search-wrap">
        <Search size={14} className="settings-shortcut-search-icon" aria-hidden />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('settings.searchShortcuts')}
          className="settings-shortcut-search"
          spellCheck={false}
        />
        <button
          type="button"
          className="settings-shortcut-resetall"
          onClick={() => { setCapturingId(null); setNotice(null); resetAllKeyboardShortcuts(); }}
        >
          {t('settings.shortcutResetAll')}
        </button>
      </div>
      <div>
        {groups.map((group) => (
          <React.Fragment key={group.id}>
            <div className="settings-shortcut-group">{t(group.labelKey)}</div>
            <div className="settings-shortcut-group-items">
            {group.commands.map((cmd) => {
          const keys = resolveShortcutKeys(cmd.id, keyboardShortcuts);
          const tokens = shortcutTokens(keys);
          const overridden = Object.prototype.hasOwnProperty.call(keyboardShortcuts, cmd.id);
          const isCapturing = capturingId === cmd.id;
          return (
            <React.Fragment key={cmd.id}>
              <div className="settings-shortcut-row">
                <span className="settings-shortcut-label">{t(cmd.labelKey)}</span>
                <div className="settings-shortcut-controls">
                  {isCapturing ? (
                    <span className="settings-shortcut-capturing">{t('settings.shortcutPressKeys')}</span>
                  ) : tokens.length ? (
                    <div className="settings-shortcut-keys">
                      {tokens.map((key, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="settings-key-sep">+</span>}
                          <kbd className="settings-key">{key}</kbd>
                        </React.Fragment>
                      ))}
                    </div>
                  ) : (
                    <span className="settings-shortcut-unbound">{t('settings.shortcutUnbound')}</span>
                  )}
                  {cmd.editable && (
                    <div className="settings-shortcut-actions">
                      <button
                        type="button"
                        className="settings-shortcut-edit"
                        onClick={() => { setNotice(null); setCapturingId(isCapturing ? null : cmd.id); }}
                      >
                        {isCapturing ? t('settings.shortcutCancel') : t('settings.shortcutEdit')}
                      </button>
                      {overridden && !isCapturing && (
                        <button
                          type="button"
                          className="settings-shortcut-reset"
                          title={t('settings.shortcutReset')}
                          aria-label={t('settings.shortcutReset')}
                          onClick={() => {
                            const displaced = resetKeyboardShortcut(cmd.id) || [];
                            setNotice(displaced.length ? { id: cmd.id, displaced } : null);
                          }}
                        >
                          <RotateCcw size={13} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {notice && notice.id === cmd.id && notice.displaced.length > 0 && (
                <div className="settings-shortcut-notice">
                  {t('settings.shortcutUnboundFrom', {
                    commands: notice.displaced
                      .map((id) => t(getShortcutCommand(id)?.labelKey || id))
                      .join(', '),
                  })}
                </div>
              )}
            </React.Fragment>
              );
            })}
            </div>
          </React.Fragment>
        ))}
      </div>
    </>
  );
};
